import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { LOT, N_LOTS } from '@hashcurve/protocol';
import { IngotCurve } from './IngotCurve';
import { useForgeHistory } from '../hooks/launch';
import { useSeen } from '../hooks/misc';
import { api } from '../lib/api';
import { buildStrip } from '../lib/strip';
import type { Strip } from '../lib/strip';

/**
 * Strip data for a list row. Without sells, forge k filled lot k exactly, so the forge list from
 * /work (at most 200 items) is the whole strip. With sells, fall back to one page of events and
 * the general reconstruction (older lots may show as unknown).
 */
export function useMiniStrip(launch: string | undefined, sold: bigint, buys: number, sells: number): Strip | null {
  const simple = sells === 0;
  const work = useQuery({
    queryKey: ['work', launch, 'mini', buys],
    enabled: !!launch && simple && buys > 0,
    staleTime: 30_000,
    queryFn: () => api.work(launch!, N_LOTS),
  });
  const history = useForgeHistory(simple ? undefined : launch, sold, BigInt(buys), false);
  return useMemo(() => {
    if (!simple) return history.strip;
    const forges = (work.data?.items ?? []).map((w, i) => ({ id: w.txHash, sold: BigInt(i + 1) * LOT, amount: LOT, difficulty: w.difficultyMetFloat }));
    return buildStrip(forges.reverse(), sold);
  }, [simple, history.strip, work.data, sold]);
}

/** Small ingot curve for list rows; loads its history once scrolled into view. */
export function MiniStrip({ launch, sold, buys, sells, label, size = 'small' }: { launch: string; sold: bigint; buys: number; sells: number; label: string; size?: 'small' | 'medium' }) {
  const [ref, seen] = useSeen<HTMLDivElement>();
  const strip = useMiniStrip(seen ? launch : undefined, sold, buys, sells);
  return (
    <div ref={ref} style={{ width: '100%' }}>
      <IngotCurve strip={strip} sold={sold} size={size} label={label} />
    </div>
  );
}
